import { Directive, OnChanges, Input, Output, EventEmitter } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormFlow, PgFormField } from '../../models/form.model';
import { DataService } from '../../services/data.service';
import { EnvironmentService } from '../../services/environment.service';
import { LocalizationService } from '../../services/localization.service';
import { NotificationsService } from '../../services/notifications.service';
import { OptionMapsService } from '../../services/option-maps.service';
import { FormsFlowState } from '../elements/forms-main/forms-main.component';
import { AuthService } from '../../services/auth.service';
import { ConfigProfileRoleActionsExtended, ConfigService } from '../../services/config.service';
import { PGUtilities } from '../../utilities/pg-utilities';

@Directive()
export abstract class StepFormBase implements OnChanges {

    abstract formId:string;
    abstract resourceId:string;

    abstract mapImportData(data:google.maps.places.PlaceResult):any
    abstract createFormFlow():FormFlow;

    formFlow:FormFlow = null;

    selectOptions:{ [select:string] : Array<{ value:string, text:string }> } = {}

    notFound = false;

    customizeFormFlow() {}

    adaptLoadData(values:any) { 
        if(values != null) {
            let _retVal = JSON.parse(JSON.stringify(values))

            for(let i in this.formFlow.states) {
                for(let _field of this.formFlow.states[i].form.fieldList) {
                    if(_field.type == 'boolean') {
                        if(/^flag_/.test(_field.name)) {
                            let _targetField = _field.name.replace(/^flag_/, '');
                            let _targetVal = values[_targetField];

                            try {
                                let _targetObj = JSON.parse(_targetVal);
                                if(_targetObj.length > 0) {
                                    _retVal[_field.name] = true;
                                }
                            }
                            catch(ex) {}
                        }
                    }
                }
            }

            return _retVal;
        } 
        else return {}    
    }

    adaptSaveData(values:any) { 
        if(values != null) {
            let _retVal = JSON.parse(JSON.stringify(values))

            let _flagFields = []

            for(let i in _retVal) {
                if(/^flag_/.test(i)) {
                    _flagFields.push(i)
                }
            }

            for(let _field of _flagFields) {
                if(!_retVal[_field]) {
                    _retVal[_field.replace(/^flag_/, '')] = null;
                }
            }

            for(let i of _flagFields) {
                delete _retVal[i];
            }

            if(typeof _retVal.languages != 'undefined') {
                _retVal.languages = PGUtilities.tryParseJSON(_retVal.languages)
            }

            return _retVal;
        } 
        else return {} 
    }    

    initData() { 
        return new Promise<null>((resolve, reject) => {
            resolve(null);
        })
    }
    
    constructor(protected optionMapsService:OptionMapsService, protected localizationService:LocalizationService, protected dataService:DataService, protected authService:AuthService, protected router:Router, protected route:ActivatedRoute, protected environmentService:EnvironmentService, protected configService:ConfigService, protected notificationsService:NotificationsService) {
    }

    actions:ConfigProfileRoleActionsExtended = null;

    private async _initFormFlow() {

        // GENERAZIONE OPTIONS DA LISTE
        this.selectOptions = this.optionMapsService.getResourceOptionMaps(this.resourceId);

        this.actions = this.configService.getResourceActionsExtended(this.resourceId)

        await this.initData()

        this.formFlow = this.createFormFlow()

        let _setFileDir:Array<PgFormField> = []

        // AUTOMATISMI: label, options, layout checkbox, switch

        for(let i in this.formFlow.states) {    

            if(this.formFlow.states[i].title == null) this.formFlow.states[i].title = 'forms-' + this.formId + '.step.' + i + '.title';
            if(this.formFlow.states[i].description == 'auto') this.formFlow.states[i].description = 'forms-' + this.formId + '.step.' + i + '.description';

            let _hasLanguages = false;
            let _hasCurrency = false;
            let _hasRealmId = false;
            let _hasGroupId = false;
            let _hasType = false;

            for(let _field of this.formFlow.states[i].form.fieldList) {                        
                if(_field.name == 'languages') {
                    _field.default = JSON.stringify(this.localizationService.defaultContentLanguages);
                    _hasLanguages = true;
                }

                if(_field.name == 'currency') {
                    _field.default = this.environmentService.getDefaultCurrency();
                    _hasCurrency = true;
                }

                if(_field.name == 'group_id') {
                    _hasGroupId = true;
                }

                if(_field.name == 'realm_id') {
                    _field.default = this.authService.user.realm?.id;
                    //_field.visible = this.authService.user.getSelectableRealms().length > 1;
                    _field.visible = false;
                    _hasRealmId = true;
                }

                if(_field.name == 'type') {
                    _hasType = true;
                }

                let _cleanFieldName = _field.name.replace(/@.*$/, '')

                for(let _prop of ['label','tooltip','placeholder']) {
                    if(_field[_prop] == 'auto') {
                        let _translated = this.localizationService.translate('RESOURCES.' + this.resourceId + '.fields.' + _cleanFieldName + '.' + _prop, null, null, true);

                        if(_translated) {
                            _field[_prop] = _translated;
                        }
                        else {
                            _field[_prop] = null;
                        }
                    }
                }
                
                if(_field.type == 'select') {                    
                    if(_field.options == null) {
                        _field.options = this.selectOptions[_field.name]
                    }

                    if(/_accessibility$/.test(_field.name)) {
                        this.formFlow.states[i].form.getFieldLayout(_field.name).display = { oneLine: true }
                    }
                    else if(_field.multi) {
                        this.formFlow.states[i].form.getFieldLayout(_field.name).display = { multiLine: true }
                    }
                }
                else if(_field.type == 'boolean') {
                    if(_field.name == 'bookable') {
                        this.formFlow.states[i].form.getFieldLayout(_field.name).display = { fullWidth: true }
                    }
                    else if(/^flag_/.test(_field.name)) {
                        let _targetField = _field.name.replace(/^flag_/, '');
                        _field.label = 'RESOURCES.' + this.resourceId + '.fields.' + _targetField + '.label';
                        _field.default = false;

                        this.formFlow.states[i].form.getFieldLayout(_field.name).display = { fullWidth: true }
                        this.formFlow.states[i].form.getFieldLayout(_targetField).condition = '$form.' + _field.name;
                    }
                }
                else if(_field.type == 'html' || _field.type == 'file' || _field.type == 'location' || _field.type == 'timetable-openings' || _field.type == 'timetable-slots' || _field.type == 'timetable-periods' || _field.type == 'timetable-dates' || _field.type == 'timetable-departures' || _field.type == 'timetable-days' || _field.type == 'timetable-event' || _field.type == 'tickets-experience' || _field.type == 'tickets-host' || _field.type == 'survey' || _field.type == 'info' || _field.type == 'text' || _field.type == 'embed') {
                    this.formFlow.states[i].form.getFieldLayout(_field.name).display = { fullWidth: true }
                }

                if(_field.type == 'file' && _field.fileDirectory == null) {
                    _setFileDir.push(_field);
                }
            }

            if(!_hasLanguages) {
                this.formFlow.states[i].form.fieldList.push(new PgFormField({ label: '', type: 'select', multi: true, name: 'languages', readonly: true }))
            }

            if(!_hasCurrency) {
                this.formFlow.states[i].form.fieldList.push(new PgFormField({ label: '', type: 'select', name: 'currency', readonly: true }))
            }

            if(!_hasGroupId) {
                this.formFlow.states[i].form.fieldList.push(new PgFormField({ label: '', type: 'select', multi: true, name: 'group_id', readonly: true }))
            }

            if(!_hasRealmId) {
                this.formFlow.states[i].form.fieldList.push(new PgFormField({ label: '', type: 'select', name: 'realm_id', readonly: true }))
            }

            if(!_hasType) {
                this.formFlow.states[i].form.fieldList.push(new PgFormField({ label: '', type: 'select', name: 'type', readonly: true }))
            }

            this.formFlow.states[i].form.resetData();
        }

        if(_setFileDir.length > 0) {
            let _resourceDir = await new Promise<string>((resolve, reject) => {
                this.dataService.resolveDirectoryPath(['default', this.resourceId]).subscribe((data) => {
                    resolve(data)
                })
            }) 

            for(let _field of _setFileDir) {
                _field.fileDirectory = _resourceDir;
            }
        }

        this.applyEnvironmentConfig();

        this.customizeFormFlow();

        for(let i in this.formFlow.states) {
            this.formFlow.states[i].form.resetData();
        }
    }
    

    applyEnvironmentConfig() {
        if(this.environmentService.environment.CustomFormConfig != null) {
            for(let _id of ['*', this.resourceId]) {
                let _config = this.environmentService.environment.CustomFormConfig[_id];

                if(_config != null) {
                    for(let i in _config) {
                        for(let j in this.formFlow.states) {
                            let _field = this.formFlow.states[j].form.getFieldByName(i);

                            if(_field != null) {
                                for(let k in _config[i]) {
                                    _field[k] = _config[i][k];
                                }

                                break;
                            }
                        }
                    }
                }
            }
        }
    }

    flowState:any = null;
    
    showImport = true;

    @Input() elementId:string = null;
    @Input() returnUrl:string = null;

    @Input() viewOnly:boolean;

    isLoading = false;

    translationJoinField:string = null;
    translationResource:string = null;

    private _translationByLanguage:{
        [language:string]: any
    } = null;

    ngOnChanges(): void {
        if(this.returnUrl == null) this.returnUrl = '/'

        this.translationResource = this.resourceId + 'Translation';
        this.translationJoinField = this.resourceId.replace(/([A-Z])/g, '_$1').replace(/^_/, '').toLowerCase() + '_id';
        this._translationByLanguage = {};

        for(let _cLanguage of this.localizationService.availableApplicationLanguages) {
            let _cLanguageBaseObj:any = {
                language: _cLanguage
            }

            _cLanguageBaseObj[this.translationJoinField] = this.elementId;

            this._translationByLanguage[_cLanguage] = _cLanguageBaseObj;
        }

        this.isLoading = true;
        this.notFound = false;

        this.flowState = null;

        this._initFormFlow().then(() => {
            if(this.dataService.isDraftId(this.elementId)) {
                this.getDraftData().subscribe((data) => {
                    if(data == null) {
                        this.isLoading = false;
                        this.notFound = true;
                        this.showImport = false;
                    }
                    else if(data.stateHistory != null) {
                        this.flowState = data;

                        if(Object.keys(data).length == 1 && !data.template) {
                            this.showImport = true;
                        }
                        else {
                            this.showImport = false;
                        }
    
                        // TODO: indagare su
                        // - perché se currentState è null esplode tutto?
                        // - perché flowState è any?
    
                        if(this.flowState.currentState == null && this.flowState.stateHistory.length > 0) {
                            this.flowState.currentState = this.flowState.stateHistory.pop();
                        }
    
                        this.onValuesChange(data.values);

                        this.afterInit().then(() => {
                            this.isLoading = false;
                        })
                    }
                    else {
                        this._loadFromValues(data).then(() => {
                            this.isLoading = false;
                        })
                    }
                })
            }
            else {
                this.dataService.getElementData(this.resourceId, this.elementId).subscribe((data) => {
                    if(data == null) {
                        this.isLoading = false;
                        this.notFound = true;
                        this.showImport = false;
                    }
                    else {
                        this.dataService.getResourceData(this.translationResource, null, this.resourceId, this.elementId).subscribe((translData) => {
                            this._loadFromValues(data, translData).then(() => {
                                this.isLoading = false;
                            })
                        })
                    }
                })
            }
        })
    }

    private async _loadFromValues(data:any, translData?:any) {
        if(Object.keys(data).length == 1 && !data.template) {
            this.showImport = true;
        }
        else {
            this.showImport = false;
        }

        if(translData == null && data.translations != null) {
            translData = data.translations;
            delete data.translations;
        }

        if(translData != null) {
            for(let _cTransl of translData) {
                this._translationByLanguage[_cTransl.language] = _cTransl;
    
                for(let i in _cTransl) {
                    if(i != 'id' && i != 'created_at' && i != 'updated_at' && i != 'deleted_at' && i != 'language') {
                        let _localeValue:any = {};
    
                        for(let _cLanguage of this.localizationService.availableApplicationLanguages) {
                            if(this._translationByLanguage[_cLanguage]) {
                                _localeValue[_cLanguage] = this._translationByLanguage[_cLanguage][i];
                            }
                            else {
                                _localeValue[_cLanguage] = {}
                            }
                        }
    
                        data[i + '@' + this.translationResource] = JSON.stringify(_localeValue);
                    }
                }
            }
        }

        this.importData = this.adaptLoadData(data);

        this.onValuesChange(this.importData);

        await this.afterInit()
    }

    async afterInit() {}

    @Output() valuesChange = new EventEmitter<any>();

    onValuesChange(values) {
        values = this.adaptSaveData(values);

        for(let i in values) {
            let _cResource = i.split('@')[1];

            if(_cResource != null) {
                if(values[i] != null) {
                    let _localeValue = JSON.parse(values[i]);

                    values[i.split('@')[0]] = _localeValue[this.localizationService.availableApplicationLanguages[0]];
                }
                else {
                    values[i.split('@')[0]] = null;
                }
            }
        }

        this.valuesChange.emit(values);
    }

    protected getDraftResourceId() {
        return this.resourceId;
    }

    protected getDraftData() {
        return this.dataService.getDraftElementData(this.getDraftResourceId(), this.elementId)
    }

    protected saveDraftData(data:any) {
        return this.dataService.saveDraftElementData(this.getDraftResourceId(), this.elementId, data)
    }

    protected deleteDraft() {
        return this.dataService.deleteDraftElement(this.getDraftResourceId(), this.elementId)
    }

    private _checkDraftSaveTimeout = null;

    draftStatus = null;

    onFlowStateChange(data:FormsFlowState) {
        if(this.dataService.isDraftId(this.elementId) && !this.isSaving) {
            if(this._checkDraftSaveTimeout) clearTimeout(this._checkDraftSaveTimeout)
            this.draftStatus = 'dirty'

            this._checkDraftSaveTimeout = setTimeout(() => {
                this._checkDraftSaveTimeout = null;
            
                this.draftStatus = 'saving'

                this.saveDraftData(data).subscribe(() => {
                    this.draftStatus = 'saved'
                }, () => {
                    this.draftStatus = 'error'
                })
            }, 1000)
        }
    }

    importData: any = null;

    setImportData(data:google.maps.places.PlaceResult) {

        if(data == null) {
            this.importData = null;
        }
        else {
            this.importData = this.mapImportData(data);
        }

        this.showImport = false;
    }

    setTemplateData(data:any) {
        if(data == null) {
            this.showImport = false;
        }
        else {
            this.isLoading = true;

            this.dataService.getResourceData(this.translationResource, null, this.resourceId, data.id).subscribe((translData) => {
                this.isLoading = false;
                let _templateData = JSON.parse(JSON.stringify(data));
    
                _templateData.id = null;
                _templateData.title = null;
                _templateData.template = 0;
                _templateData.certificate = null;
    
                this._loadFromValues(_templateData, translData)
            })
        }
    }

    isSaving = false;

    onSaveData(values:any) {
        if(!this.isSaving) {
            this.isSaving = true;

            values = this.adaptSaveData(values);

            let _keysToRemove = [];

            for(let i in values) {
                let _cResource = i.split('@')[1];

                if(_cResource != null) {
                    _keysToRemove.push(i);

                    if(values[i] != null) {
                        let _localeValue = JSON.parse(values[i]);

                        for(let _cLanguage in _localeValue) {
                            if(this._translationByLanguage[_cLanguage] != null) {
                                this._translationByLanguage[_cLanguage][i.split('@')[0]] = _localeValue[_cLanguage]
                            }
                        }

                        for(let _cLanguage of this.localizationService.availableApplicationLanguages) {
                            // prendo la prima lingua che ha un valore e la uso come valore del campo di base
                            if(_localeValue[_cLanguage] != null && _localeValue[_cLanguage] != '') {
                                values[i.split('@')[0]] = _localeValue[_cLanguage];
                                break;
                            }
                        }
                    }
                    else {
                        values[i.split('@')[0]] = null;
                    }
                }
            }

            for(let _cKey of _keysToRemove) {
                delete values[_cKey];
            }

            this.dataService.saveElementData(this.resourceId, this.elementId, values).subscribe((data) => {
                if(data == null) {
                    this.isSaving = false;
                }
                else {
                    let _cAction:'update'|'insert' = 'update';
                    
                    if(values.id != data.id) {
                        _cAction = 'insert'
                    }

                    this.dataService.saveElementTranslations(data.id, this.translationResource, this.translationJoinField, data.languages, this._translationByLanguage).subscribe(() => {
                        this.isSaving = false;

                        if(data != null) {
                            if(this.resourceId == 'Experience' && _cAction == 'insert' && !data.published && !this.actions.publish) { // TODO: questo andrebbe spostato nella form di experience in qualche modo
                                this.notificationsService.addLocalNotification('forms-result-splash.action-text-' + _cAction + '-experience-unpublished', 'success')
                            }
                            else {
                                this.notificationsService.addLocalNotification('forms-result-splash.action-text-' + _cAction, 'success', null, null, 3000)
                            }

                            this.doFormReturn(_cAction, data.id)
                        }
                    })
                }
            })
        }
    }

    onCancelEdit() {
        if(!this.isSaving) {
            this.doFormReturn('cancel')
        }
    }

    onResetForm() {
        if(!this.isSaving) {
            this.isSaving = true;

            this.deleteDraft().subscribe((data) => {
                this.isSaving = false;

                if(data) {
                    this.doFormReturn('reset')
                }
            })
        }
    }

    onDeleteData() {
        if(!this.isSaving) {
            this.isSaving = true;

            this.dataService.deleteElement(this.resourceId, this.elementId).subscribe((data) => {
                this.isSaving = false;

                if(data) {
                    this.notificationsService.addLocalNotification('forms-result-splash.action-text-delete', 'success', null, null, 3000)
                    this.doFormReturn('delete')
                }
            })
        }
    }

    @Input() inModal:boolean;

    @Output() formReturn = new EventEmitter<{ action:string, id:string }>();

    doFormReturn(action:string, id?:string) {
        this.formReturn.emit({ action: action, id: id || this.elementId })

        if(!this.inModal) {
            this.router.navigate([this.returnUrl], { relativeTo: this.route })
        }
    }
}